import { Box, Stack, TextField, Button, Alert, useMediaQuery, useTheme } from '@mui/material';
import { useState } from 'react';

export interface ReplyFormData {
  content: string;
  parentId?: number;
}

interface ReplyFormProps {
  onSubmit: (data: ReplyFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string;
  parentId?: number;
  placeholder?: string;
  compact?: boolean;
}

export const ReplyForm: React.FC<ReplyFormProps> = ({
  onSubmit,
  onCancel,
  isLoading = false,
  error,
  parentId,
  placeholder = 'Write your reply...',
  compact = false,
}) => {
  const [content, setContent] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper function to determine number of rows
  const getRowCount = () => {
    if (compact) {
      return isMobile ? 2 : 3;
    }
    return isMobile ? 3 : 4;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!content.trim()) {
      return;
    }

    try {
      await onSubmit({
        content: content.trim(),
        parentId,
      });
      setContent('');
    } catch (submitError) {
      // Error handling is managed by the parent component
    }
  };

  const handleCancel = () => {
    setContent('');
    onCancel();
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: compact ? 'none' : '0px 2px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Stack spacing={{ xs: 1.5, sm: 2 }}>
        {error && (
          <Alert
            severity="error"
            sx={{ borderRadius: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {error}
          </Alert>
        )}

        <TextField
          multiline
          rows={getRowCount()}
          placeholder={placeholder}
          value={content}
          onChange={e => setContent(e.target.value)}
          disabled={isLoading}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              minHeight: { xs: '80px', sm: 'auto' },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
                borderWidth: 2,
              },
            },
            '& .MuiOutlinedInput-input': {
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
              lineHeight: 1.4,
              py: { xs: 0.5, sm: 0.5 },
              px: { xs: 1, sm: 1 },
            },
          }}
        />

        <Stack direction="row" spacing={{ xs: 1, sm: 2 }} justifyContent="flex-end">
          <Button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            sx={{
              color: 'secondary.600',
              borderColor: 'secondary.300',
              textTransform: 'none',
              fontWeight: 500,
              px: { xs: 1.5, sm: 3 },
              py: { xs: 0.75, sm: 1 },
              borderRadius: 1,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              minHeight: { xs: '36px', sm: 'auto' },
              '&:hover': {
                bgcolor: 'secondary.100',
                borderColor: 'secondary.400',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !content.trim()}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              textTransform: 'none',
              fontWeight: 500,
              px: { xs: 1.5, sm: 3 },
              py: { xs: 0.75, sm: 1 },
              borderRadius: 1,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              minHeight: { xs: '36px', sm: 'auto' },
              boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.2)',
              '&:hover': {
                bgcolor: 'primary.dark',
                boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.3)',
              },
              '&:disabled': {
                bgcolor: 'secondary.300',
                color: 'secondary.500',
                boxShadow: 'none',
              },
            }}
          >
            {isLoading ? 'Posting...' : 'Reply'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
